import React, { useEffect, useState, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { collection, getDocs, query, orderBy } from "firebase/firestore";
import { db } from "../../db/firebase";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { HiOutlineMusicalNote, HiOutlineMagnifyingGlass, HiOutlineChevronDown, HiOutlineSquares2X2, HiOutlineListBullet, HiOutlineChevronLeft, HiOutlineChevronRight } from "react-icons/hi2";
import Skeleton from "react-loading-skeleton";
import { SongArtwork } from "../../components/SongArtwork";
import "react-loading-skeleton/dist/skeleton.css";

const fetchAllSongs = async (
  setSongs: (value: any[]) => void,
  setLoading: (value: boolean) => void,
  setError: (value: string) => void,
  t: (key: string) => string
) => {
  setLoading(true);
  try {
    const songCollection = collection(db, "songs");
    const songQuery = query(songCollection, orderBy("title"));

    const songSnapshot = await getDocs(songQuery);
    const songList = songSnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));

    setSongs(songList);
    setError("");
  } catch (err) {
    console.error(t("errorFetchingSongs"), err);
    setError(t("failedToFetchSongs"));
  } finally {
    setLoading(false);
  }
};

const SongDatabase: React.FC = () => {
  const { t } = useTranslation();
  const [songs, setSongs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [selectedArtist, setSelectedArtist] = useState<string | null>(null);
  const [artistDropdownOpen, setArtistDropdownOpen] = useState(false);
  const [artistSearchQuery, setArtistSearchQuery] = useState("");
  const artistDropdownRef = useRef<HTMLDivElement>(null);
  const artistButtonRef = useRef<HTMLButtonElement>(null);
  const artistDropdownPortalRef = useRef<HTMLDivElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 24;

  useEffect(() => {
    fetchAllSongs(setSongs, setLoading, setError, t);
  }, [t]);

  const allArtists = useMemo(() => {
    const artists = new Set<string>();
    songs.forEach((s) => {
      const artist = (s.artist || "").trim();
      if (artist) artists.add(artist);
    });
    return Array.from(artists).sort((a, b) => a.localeCompare(b));
  }, [songs]);

  const filteredArtists = useMemo(() => {
    const q = artistSearchQuery.trim().toLowerCase();
    if (!q) return allArtists;
    return allArtists.filter((a) => a.toLowerCase().includes(q));
  }, [allArtists, artistSearchQuery]);

  const filteredSongs = useMemo(() => {
    let result = songs;
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(
        (s) =>
          (s.title || "").toLowerCase().includes(q) ||
          (s.artist || "").toLowerCase().includes(q) ||
          (s.album || "").toLowerCase().includes(q)
      );
    }
    if (selectedArtist) {
      result = result.filter((s) => (s.artist || "").trim() === selectedArtist);
    }
    return result;
  }, [songs, search, selectedArtist]);

  const totalPages = Math.max(1, Math.ceil(filteredSongs.length / ITEMS_PER_PAGE));
  const paginatedSongs = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredSongs.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredSongs, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, selectedArtist]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      const inButton = artistDropdownRef.current?.contains(target);
      const inDropdown = artistDropdownPortalRef.current?.contains(target);
      if (!inButton && !inDropdown) {
        setArtistDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (artistDropdownOpen && artistButtonRef.current) {
      const updatePosition = () => {
        if (artistButtonRef.current) {
          const rect = artistButtonRef.current.getBoundingClientRect();
          setDropdownPosition({
            top: rect.bottom + 6,
            left: rect.left,
            width: rect.width,
          });
        }
      };
      updatePosition();
      window.addEventListener("scroll", updatePosition, true);
      window.addEventListener("resize", updatePosition);
      return () => {
        window.removeEventListener("scroll", updatePosition, true);
        window.removeEventListener("resize", updatePosition);
      };
    }
  }, [artistDropdownOpen]);

  if (loading) {
    return (
      <div className="flex gap-6 max-w-6xl mx-auto">
        <Skeleton className="w-72 shrink-0 h-64 rounded-2xl" />
        <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Skeleton key={i} height={200} className="rounded-2xl" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col lg:flex-row gap-6 max-w-6xl mx-auto">
        {/* Left: Filter panel */}
        <aside className="w-full lg:w-72 shrink-0">
          <div className="rounded-2xl border border-gray-200/70 dark:border-gray-700/70 bg-white/70 dark:bg-gray-800/50 backdrop-blur-sm sticky top-4">
            <div className="px-6 py-5 border-b border-gray-100/80 dark:border-gray-700/80">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {filteredSongs.length} {t("songs")?.toLowerCase() || "songs"}
              </p>
            </div>
            <div className="px-6 py-3 border-b border-gray-100/80 dark:border-gray-700/80">
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                {t("search")}
              </span>
            </div>
            <div className="p-6 space-y-4">
              <div className="relative">
                <HiOutlineMagnifyingGlass className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 dark:text-gray-500" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={t("search") + "..."}
                  className="w-full pl-12 pr-4 py-3 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 placeholder-gray-400 focus:ring-2 focus:ring-[#5932EA]/30 focus:border-[#5932EA] transition-all"
                />
              </div>
              <div className="relative" ref={artistDropdownRef}>
                <button
                  ref={artistButtonRef}
                  type="button"
                  onClick={() => setArtistDropdownOpen((o) => !o)}
                  className="flex items-center gap-2 w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 hover:border-gray-300 dark:hover:border-gray-500 transition-colors text-left"
                >
                  <span className="truncate flex-1">
                    {selectedArtist || (t("allArtists") || "All artists")}
                  </span>
                  <HiOutlineChevronDown className={`w-5 h-5 shrink-0 transition-transform ${artistDropdownOpen ? "rotate-180" : ""}`} />
                </button>
                {artistDropdownOpen &&
                  createPortal(
                    <div
                      ref={artistDropdownPortalRef}
                      className="fixed rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 shadow-xl z-[9999] overflow-hidden max-h-72 flex flex-col"
                      style={{
                        top: dropdownPosition.top,
                        left: dropdownPosition.left,
                        width: dropdownPosition.width,
                      }}
                    >
                      <div className="p-2 border-b border-gray-100 dark:border-gray-700 shrink-0">
                        <input
                          type="text"
                          value={artistSearchQuery}
                          onChange={(e) => setArtistSearchQuery(e.target.value)}
                          placeholder={`${t("search")} ${t("artist")}...`}
                          className="w-full px-3 py-2 rounded-lg text-sm border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-gray-200 placeholder-gray-400 focus:ring-2 focus:ring-[#5932EA]/30 focus:border-[#5932EA]"
                          autoFocus
                        />
                      </div>
                      <ul className="overflow-y-auto py-1 max-h-56">
                        <li>
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedArtist(null);
                              setArtistDropdownOpen(false);
                              setArtistSearchQuery("");
                            }}
                            className={`w-full px-4 py-2.5 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 ${!selectedArtist ? "bg-[#5932EA]/10 text-[#5932EA] font-medium" : "text-gray-700 dark:text-gray-300"}`}
                          >
                            {t("allArtists") || "All artists"}
                          </button>
                        </li>
                        {filteredArtists.map((artist) => (
                          <li key={artist}>
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedArtist(artist);
                                setArtistDropdownOpen(false);
                                setArtistSearchQuery("");
                              }}
                              className={`w-full px-4 py-2.5 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 truncate ${selectedArtist === artist ? "bg-[#5932EA]/10 text-[#5932EA] font-medium" : "text-gray-700 dark:text-gray-300"}`}
                            >
                              {artist}
                            </button>
                          </li>
                        ))}
                        {filteredArtists.length === 0 && (
                          <li className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                            {t("noArtistsFound") || "No artists found"}
                          </li>
                        )}
                      </ul>
                    </div>,
                    document.body
                  )}
              </div>
            </div>
          </div>
        </aside>

        {/* Right: Song database */}
        <main className="flex-1 min-w-0">
          {error && (
            <div className="mb-6 p-4 rounded-xl bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400">
              {error}
            </div>
          )}
          <div className="rounded-2xl border border-gray-200/70 dark:border-gray-700/70 bg-white/70 dark:bg-gray-800/50 backdrop-blur-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100/80 dark:border-gray-700/80 flex items-center justify-between gap-4 flex-wrap">
              <h2 className="text-base font-semibold text-gray-800 dark:text-gray-100">
                {t("songDatabase")}
              </h2>
              <div className="inline-flex p-1 rounded-xl bg-gray-100/80 dark:bg-gray-900/40 border border-gray-200/60 dark:border-gray-700/60">
                <button
                  type="button"
                  onClick={() => setViewMode("grid")}
                  className={`p-2 rounded-lg transition-all ${viewMode === "grid" ? "bg-white dark:bg-gray-700 text-[#5932EA] shadow-sm" : "text-gray-500 dark:text-gray-400"}`}
                  title={t("gridView") || "Grid view"}
                  aria-label={t("gridView") || "Grid view"}
                >
                  <HiOutlineSquares2X2 className="w-5 h-5" />
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode("list")}
                  className={`p-2 rounded-lg transition-all ${viewMode === "list" ? "bg-white dark:bg-gray-700 text-[#5932EA] shadow-sm" : "text-gray-500 dark:text-gray-400"}`}
                  title={t("listView") || "List view"}
                  aria-label={t("listView") || "List view"}
                >
                  <HiOutlineListBullet className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="p-6">
              {filteredSongs.length > 0 ? (
                <>
                  {viewMode === "grid" ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                      {paginatedSongs.map((song) => (
                        <Link
                          key={song.id}
                          to={`/songs/${song.id}`}
                          className="group rounded-xl border border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50 overflow-hidden hover:border-[#5932EA]/50 hover:shadow-md transition-all"
                        >
                          <div className="aspect-square bg-gray-200 dark:bg-gray-600 flex items-center justify-center overflow-hidden">
                            <SongArtwork
                              src={song.artworkURL}
                              alt={song.title}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                              iconClassName="w-16 h-16"
                            />
                          </div>
                          <div className="p-4">
                            <h3 className="font-semibold text-gray-800 dark:text-gray-100 line-clamp-2">
                              {song.title}
                            </h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 truncate">
                              {song.artist || "—"}
                            </p>
                            {song.album && (
                              <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 truncate">
                                {song.album}
                              </p>
                            )}
                          </div>
                        </Link>
                      ))}
                    </div>
                  ) : (
                    <ul className="space-y-2">
                      {paginatedSongs.map((song) => (
                        <li key={song.id}>
                          <Link
                            to={`/songs/${song.id}`}
                            className="flex items-center gap-4 p-4 rounded-xl border border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50 hover:border-[#5932EA]/50 hover:shadow-md transition-all group"
                          >
                            <div className="w-14 h-14 shrink-0 rounded-xl bg-gray-200 dark:bg-gray-600 flex items-center justify-center overflow-hidden">
                              <SongArtwork
                                src={song.artworkURL}
                                alt={song.title}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                                iconClassName="w-8 h-8"
                              />
                            </div>
                            <div className="min-w-0 flex-1">
                              <h3 className="font-semibold text-gray-800 dark:text-gray-100 truncate">
                                {song.title}
                              </h3>
                              <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                                {song.artist || "—"}
                              </p>
                              {song.album && (
                                <p className="text-xs text-gray-400 dark:text-gray-500 truncate">
                                  {song.album}
                                </p>
                              )}
                            </div>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="mt-6 flex items-center justify-between gap-4 flex-wrap">
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {t("showing") || "Showing"} {(currentPage - 1) * ITEMS_PER_PAGE + 1}–{Math.min(currentPage * ITEMS_PER_PAGE, filteredSongs.length)} {t("of") || "of"} {filteredSongs.length}
                      </p>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                          disabled={currentPage <= 1}
                          className="p-2 rounded-lg border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                          aria-label={t("previous")}
                        >
                          <HiOutlineChevronLeft className="w-5 h-5" />
                        </button>
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300 px-3">
                          {currentPage} / {totalPages}
                        </span>
                        <button
                          type="button"
                          onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                          disabled={currentPage >= totalPages}
                          className="p-2 rounded-lg border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                          aria-label={t("next")}
                        >
                          <HiOutlineChevronRight className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="flex flex-col items-center justify-center py-16 text-gray-500 dark:text-gray-400">
                  <HiOutlineMusicalNote className="w-14 h-14 mb-3 opacity-40" strokeWidth={1.5} />
                  <p className="font-medium">
                    {search.trim() ? (t("noResults") || "No results found") : (t("noSongsYet") || "No songs yet")}
                  </p>
                </div>
              )}
            </div>
          </div>
        </main>
    </div>
  );
};

export default SongDatabase;
